import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, MapPin, Search, XCircle, FileText, Printer, Pencil, Trash2, IndianRupee, Loader2, X } from "lucide-react";
import api, { inr, fmtDate, fmtErr } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import ShipmentDetailModal from "../../components/ShipmentDetailModal";
import ShareTrackingActions from "../../components/ShareTrackingActions";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["", "LABEL_GENERATED", "PICKUP_SCHEDULED", "PICKED_UP", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED", "NDR", "RTO", "CANCELLED"];
const CANCELLABLE = ["ORDER_CREATED", "SHIPMENT_CREATED", "AWB_ASSIGNED", "LABEL_GENERATED", "PICKUP_SCHEDULED"];

const selectCls = "rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-shipany-navy focus:border-shipany-blue focus:outline-none";

export default function Shipments() {
  const [shipments, setShipments] = useState(null);
  const [filters, setFilters] = useState({ status: "", payment_mode: "", awb: "", order_id: "" });
  const [pdfMenu, setPdfMenu] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [billFor, setBillFor] = useState(null);
  const [markup, setMarkup] = useState("");
  const [billBusy, setBillBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const loadDrafts = useCallback(() => {
    api.get("/drafts").then(({ data }) => setDrafts(data)).catch(() => {});
  }, []);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const removeDraft = async (id) => {
    try {
      await api.delete(`/drafts/${id}`);
      toast.success("Draft deleted");
      loadDrafts();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/shipments", { params });
      setShipments(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const copyAwb = (awb) => {
    navigator.clipboard?.writeText(awb);
    toast.success("AWB copied");
  };

  const cancel = async (s) => {
    if (!window.confirm(`Cancel shipment ${s.awb}? The courier cancellation will be attempted first. Refund is issued only after the courier accepts it.`)) return;
    try {
      const { data } = await api.post(`/shipments/${s.shipment_id}/cancel`);
      if (data.pending) {
        toast.warning(data.message || data.provider_error || "Cancellation is pending courier confirmation");
      } else if (data.already_cancelled) {
        toast.info("Shipment is already cancelled");
      } else {
        toast.success(`Cancelled — ${inr(data.refunded)} refunded`);
      }
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openBill = (s) => {
    setBillFor(s);
    setMarkup("");
  };
  const billTotal = (billFor?.price || 0) + (parseFloat(markup) || 0);

  const downloadBill = async () => {
    setBillBusy(true);
    try {
      const res = await api.post(`/shipments/${billFor.shipment_id}/bill`, {
        extra_amount: parseFloat(markup) || 0,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bill-${billFor.awb || billFor.order_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Bill downloaded — ready to print for your receiver");
      setBillFor(null);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBillBusy(false);
    }
  };

  return (
    <div data-testid="shipments-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Shipments</h1>
          <p className="text-sm text-slate-500">{shipments?.length ?? 0} shipments</p>
        </div>
        <Link to="/app/create-shipment" data-testid="shipments-create-btn" className="rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">+ Create Shipment</Link>
      </div>

      {drafts.length > 0 && (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="shipment-drafts">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-700">Saved drafts — assign a courier anytime</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {drafts.map((d) => (
              <div key={d.draft_id} className="rounded-xl border border-amber-200 bg-white p-4" data-testid={`draft-card-${d.draft_id}`}>
                <p className="font-semibold text-shipany-navy">{d.summary?.receiver || "Unnamed receiver"}</p>
                <p className="mono mt-0.5 text-xs text-slate-500">{d.summary?.pincode || "—"} · saved {fmtDate(d.updated_at)}</p>
                <div className="mt-3 flex gap-2">
                  <Link to={`/app/create-shipment?draft=${d.draft_id}`} data-testid={`draft-resume-${d.draft_id}`}
                    className="flex items-center gap-1.5 rounded-lg bg-shipany-blue px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0648B3]"><Pencil className="h-3 w-3" /> Resume & Assign Courier</Link>
                  <button onClick={() => removeDraft(d.draft_id)} data-testid={`draft-delete-${d.draft_id}`}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50"><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-3" data-testid="shipment-filters">
        <select data-testid="filter-status" className={selectCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All statuses"}</option>)}
        </select>
        <select data-testid="filter-payment" className={selectCls} value={filters.payment_mode} onChange={(e) => setFilters({ ...filters, payment_mode: e.target.value })}>
          <option value="">COD + Prepaid</option><option>Prepaid</option><option>COD</option>
        </select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="filter-awb" className={`${selectCls} mono w-44 pl-9`} placeholder="AWB" value={filters.awb} onChange={(e) => setFilters({ ...filters, awb: e.target.value })} />
        </div>
        <input data-testid="filter-order-id" className={`${selectCls} mono w-44`} placeholder="Order ID" value={filters.order_id} onChange={(e) => setFilters({ ...filters, order_id: e.target.value })} />
      </div>

      <div className="mt-5 space-y-3 md:hidden" data-testid="shipments-mobile-list">
        {(shipments || []).map((s) => (
          <article key={s.shipment_id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`shipment-card-${s.awb}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <button onClick={() => setDetailId(s.shipment_id)} className="mono break-all text-left text-xs font-semibold text-slate-500 underline decoration-slate-300 underline-offset-2" data-testid={`mobile-order-detail-${s.shipment_id}`}>{s.order_number}</button>
                <button onClick={() => copyAwb(s.awb)} className="mono mt-1 flex items-center gap-1.5 break-all text-sm font-semibold text-shipany-blue" data-testid={`mobile-copy-awb-${s.awb}`}>{s.awb} <Copy className="h-3 w-3" /></button>
              </div>
              <StatusBadge status={s.status} />
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div><dt className="text-slate-400">Courier</dt><dd className="mt-0.5 font-semibold text-shipany-navy">{s.courier_name}</dd></div>
              <div><dt className="text-slate-400">Route</dt><dd className="mt-0.5 font-semibold text-shipany-navy">{s.origin_city} → {s.destination_city}</dd></div>
              <div><dt className="text-slate-400">Weight</dt><dd className="mono mt-0.5 font-semibold">{s.weight} kg</dd></div>
              <div><dt className="text-slate-400">Price</dt><dd className="mono mt-0.5 font-semibold">{inr(s.price)}</dd></div>
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={`/track/${s.awb}`} data-testid={`mobile-track-${s.awb}`} className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">Track</Link>
              <ShareTrackingActions code={s.awb} label="shipment" testId={`share-mobile-${s.awb}`} compact />
              <a href={`${API_BASE}/api/shipments/${s.shipment_id}/document?type=label`} target="_blank" rel="noreferrer" data-testid={`mobile-label-${s.awb}`} className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">{Math.max(parseInt(s.label_count || s.boxes || 1), 1) > 1 ? "Labels PDF" : "Label"}</a>
              <a href={`${API_BASE}/api/shipments/${s.shipment_id}/document?type=invoice`} target="_blank" rel="noreferrer" data-testid={`mobile-invoice-${s.awb}`} className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">Invoice</a>
              <button onClick={() => openBill(s)} data-testid={`mobile-bill-${s.awb}`} className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700">Bill</button>
              <div className="relative">
                <button onClick={() => setPdfMenu(pdfMenu === `m-${s.shipment_id}` ? null : `m-${s.shipment_id}`)} title="ShipAny Shipper Label (scannable PDF — A4 or 4×6 thermal)"
                  data-testid={`mobile-pdf-label-btn-${s.awb}`} className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">Print Label</button>
                {pdfMenu === `m-${s.shipment_id}` && (
                  <>
                    <button aria-label="Close label menu" className="fixed inset-0 z-10 cursor-default" onClick={() => setPdfMenu(null)} />
                    <div className="absolute left-0 z-20 mt-1 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid={`mobile-pdf-label-menu-${s.awb}`}>
                      <a href={`${API_BASE}/api/shipments/${s.shipment_id}/shipper-label.pdf?paper=a4`} target="_blank" rel="noreferrer" onClick={() => setPdfMenu(null)}
                        data-testid={`mobile-pdf-label-a4-${s.awb}`} className="block px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 hover:text-shipany-blue">A4 Sheet (PDF)</a>
                      <a href={`${API_BASE}/api/shipments/${s.shipment_id}/shipper-label.pdf?paper=4x6`} target="_blank" rel="noreferrer" onClick={() => setPdfMenu(null)}
                        data-testid={`mobile-pdf-label-4x6-${s.awb}`} className="block border-t border-slate-100 px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 hover:text-shipany-blue">4×6 Label — thermal (PDF)</a>
                    </div>
                  </>
                )}
              </div>
              {!["CANCELLED", "DELIVERED", "RTO", "RTO_DELIVERED", "RETURNED"].includes(s.status) && <button onClick={() => cancel(s)} data-testid={`mobile-cancel-${s.awb}`} className="rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-600">Cancel</button>}
            </div>
          </article>
        ))}
        {shipments && shipments.length === 0 && <p className="rounded-2xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-400">No shipments match your filters.</p>}
      </div>

      <div className="mt-5 hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white md:block" data-testid="shipments-table">
        <table className="w-full min-w-[980px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Order / AWB</th><th className="px-4 py-3.5">Courier</th><th className="px-4 py-3.5">Route</th>
              <th className="px-4 py-3.5">Weight</th><th className="px-4 py-3.5">Price</th><th className="px-4 py-3.5">Value</th><th className="px-4 py-3.5">Mode</th>
              <th className="px-4 py-3.5">Status</th><th className="px-4 py-3.5">Created</th><th className="px-4 py-3.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(shipments || []).map((s) => (
              <tr key={s.shipment_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`shipment-row-${s.awb}`}>
                <td className="px-5 py-3.5">
                  <button onClick={() => setDetailId(s.shipment_id)} className="mono text-left text-xs font-semibold text-slate-500 underline decoration-slate-300 underline-offset-2 transition-colors hover:text-shipany-blue" data-testid={`order-detail-${s.shipment_id}`}>{s.order_number}</button>
                  <button onClick={() => copyAwb(s.awb)} className="mono flex items-center gap-1.5 font-semibold text-shipany-blue" data-testid={`copy-awb-${s.awb}`}>{s.awb} <Copy className="h-3 w-3" /></button>
                </td>
                <td className="px-4 py-3.5">{s.courier_name}</td>
                <td className="px-4 py-3.5 text-slate-500">{s.origin_city} → {s.destination_city}</td>
                <td className="mono px-4 py-3.5">{s.weight} kg</td>
                <td className="mono px-4 py-3.5 font-semibold">{inr(s.price)}</td>
                <td className="mono px-4 py-3.5" data-testid={`shipment-value-${s.awb}`}>{s.declared_value ? inr(s.declared_value) : "—"}</td>
                <td className="px-4 py-3.5">{s.payment_mode}{s.payment_mode === "COD" ? ` (${inr(s.cod_amount)})` : ""}</td>
                <td className="px-4 py-3.5">
                  <div className="flex flex-col items-start gap-1.5">
                    <StatusBadge status={s.status} />
                    {s.cancellation_pending && (
                      <span data-testid={`cancellation-pending-${s.awb}`} className="whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200">
                        Cancellation pending
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3.5 text-slate-500">{fmtDate(s.created_at)}</td>
                <td className="px-4 py-3.5">
                  <div className="flex gap-1">
                    <Link to={`/track/${s.awb}`} title="Track" data-testid={`track-${s.awb}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue"><MapPin className="h-4 w-4" /></Link>
                    <ShareTrackingActions code={s.awb} label="shipment" testId={`share-${s.awb}`} compact />
                    <a href={`${API_BASE}/api/shipments/${s.shipment_id}/document?type=label`} target="_blank" rel="noreferrer"
                      title={Math.max(parseInt(s.label_count || s.boxes || 1), 1) > 1 ? "Combined box labels (PDF)" : "Shipping Label (original)"}
                      data-testid={`label-${s.awb}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue"><Download className="h-4 w-4" /></a>
                    <div className="relative">
                      <button onClick={() => setPdfMenu(pdfMenu === s.shipment_id ? null : s.shipment_id)} title="ShipAny Shipper Label (scannable PDF — A4 or 4×6 thermal)"
                        data-testid={`pdf-label-btn-${s.awb}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue"><Printer className="h-4 w-4" /></button>
                      {pdfMenu === s.shipment_id && (
                        <>
                          <button aria-label="Close label menu" className="fixed inset-0 z-10 cursor-default" onClick={() => setPdfMenu(null)} />
                          <div className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid={`pdf-label-menu-${s.awb}`}>
                            <a href={`${API_BASE}/api/shipments/${s.shipment_id}/shipper-label.pdf?paper=a4`} target="_blank" rel="noreferrer" onClick={() => setPdfMenu(null)}
                              data-testid={`pdf-label-a4-${s.awb}`} className="block px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 hover:text-shipany-blue">A4 Sheet (PDF)</a>
                            <a href={`${API_BASE}/api/shipments/${s.shipment_id}/shipper-label.pdf?paper=4x6`} target="_blank" rel="noreferrer" onClick={() => setPdfMenu(null)}
                              data-testid={`pdf-label-4x6-${s.awb}`} className="block border-t border-slate-100 px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 hover:text-shipany-blue">4×6 Label — thermal (PDF)</a>
                          </div>
                        </>
                      )}
                    </div>
                    <a href={`${API_BASE}/api/shipments/${s.shipment_id}/document?type=invoice`} target="_blank" rel="noreferrer" title="Invoice" data-testid={`invoice-${s.awb}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue"><FileText className="h-4 w-4" /></a>
                    <button onClick={() => openBill(s)} title="Customer bill with your extra amount" data-testid={`bill-${s.awb}`}
                      className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"><IndianRupee className="h-4 w-4" /></button>
                    {!["CANCELLED", "DELIVERED", "RTO", "RTO_DELIVERED", "RETURNED"].includes(s.status) && (
                      <button onClick={() => cancel(s)} data-testid={`cancel-${s.awb}`}
                        title={s.cancellation_pending ? "Retry cancellation now" : CANCELLABLE.includes(s.status) ? "Cancel shipment — free until courier pickup" : "Try cancel — courier live status decides (possible only before pickup)"}
                        className={`rounded-lg p-2 transition-colors ${CANCELLABLE.includes(s.status) ? "text-red-400 hover:bg-red-50 hover:text-red-600" : "text-slate-300 hover:bg-amber-50 hover:text-amber-600"}`}><XCircle className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {shipments && shipments.length === 0 && <tr><td colSpan={10} className="px-6 py-12 text-center text-slate-400">No shipments match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {detailId && <ShipmentDetailModal shipmentId={detailId} onClose={() => setDetailId(null)} />}

      {billFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="customer-bill-modal">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Customer Bill — {billFor.awb || billFor.order_number}</h2>
              <button onClick={() => setBillFor(null)} data-testid="customer-bill-close" className="text-slate-400 transition-colors hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-xs text-slate-500">The PDF uses your saved customer profile, pickup address and support phone as the bill-from details.</p>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5 text-sm" data-testid="customer-bill-base-row">
                <span className="font-semibold text-shipany-navy">Shipment amount</span>
                <span className="mono font-bold">{inr(billFor.price || 0)}</span>
              </div>
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-500" htmlFor="customer-bill-markup-input">Extra amount to add</label>
              <input id="customer-bill-markup-input" value={markup} onChange={(e) => setMarkup(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="0.00" inputMode="decimal" data-testid="customer-bill-markup-input"
                className="mono w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" />
            </div>

            <div className="mt-4 flex items-center justify-between rounded-xl bg-shipany-navy px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-300">Final bill amount</span>
              <span className="mono text-lg font-bold text-white" data-testid="customer-bill-total">{inr(billTotal)}</span>
            </div>
            <button onClick={downloadBill} disabled={billBusy} data-testid="customer-bill-download-btn"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white transition-shadow hover:shadow-lg disabled:opacity-60">
              {billBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Download Bill (PDF)
            </button>
            <p className="mt-2 text-center text-[10px] text-slate-400">One-time print — includes customer and receiver name/address, AWB, ShipAny tracking link, support phone, weight, item name(s), fixed terms and ONE final amount. Nothing is stored.</p>
          </div>
        </div>
      )}
    </div>
  );
}
